import { ReactElement, ReactNode, useEffect, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useTheme } from '@mui/material/styles';

import { white, neutral } from '@localstack/integrations';

import { useAuthProvider } from '~/hooks/useAuthProvider';

interface Props {
  children: ReactNode;
}

const SURVEY_APPEARANCES = {
  light: {
    backgroundColor: neutral[50],
    textColor: neutral[950],
    borderColor: neutral[300],
    inputBackgroundColor: white,
    inputHoverBorderColor: neutral[400],
    submitButtonColor: neutral[200],
    submitButtonTextColor: neutral[950],
  },
  dark: {
    backgroundColor: neutral[900],
    textColor: neutral[300],
    borderColor: neutral[700],
    inputBackgroundColor: neutral[800],
    inputHoverBorderColor: neutral[500],
    submitButtonColor: neutral[600],
    submitButtonTextColor: neutral[50],
    posthogIconColor: neutral[300],
  },
} as const;

function applyStylesToSurvey(surveyEl: Element, mode: 'light' | 'dark') {
  const appearance = SURVEY_APPEARANCES[mode];
  const shadowRoot = surveyEl.shadowRoot;

  if (!shadowRoot) return false;

  let styleEl = shadowRoot.getElementById('ph-theme-override') as HTMLStyleElement | null;

  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'ph-theme-override';
    shadowRoot.appendChild(styleEl);
  }

  const iconOverride =
    'posthogIconColor' in appearance
      ? `.survey-form svg path, .thank-you-message svg path { fill: ${appearance.posthogIconColor} !important; }`
      : '';

  styleEl.textContent = `
    .survey-form, .thank-you-message, .form-cancel {
      background-color: ${appearance.backgroundColor} !important;
      color: ${appearance.textColor} !important;
      border-color: ${appearance.borderColor} !important;
    }

    ${iconOverride}

    .survey-question, .thank-you-message-header {
      color: ${appearance.textColor} !important;
    }

    .form-submit {
      background-color: ${appearance.submitButtonColor} !important;
      color: ${appearance.submitButtonTextColor} !important;
    }

    .multiple-choice-options label {
      background-color: ${appearance.inputBackgroundColor} !important;
      border-color: ${appearance.borderColor} !important;
      color: ${appearance.textColor} !important;
    }

    .multiple-choice-options input[type=radio], .multiple-choice-options input[type=checkbox] {
      background-color: ${appearance.inputBackgroundColor} !important;
      border-color: ${appearance.textColor} !important;
    }
    
    .multiple-choice-options input[type=checkbox]:checked:after, .multiple-choice-options input[type=radio]:checked:after {
          background-color: ${appearance.inputBackgroundColor} !important;
border-color: ${appearance.textColor} !important;
    }

    .multiple-choice-options input[type=radio]:checked {
      background-color: ${appearance.textColor} !important;
    }

    :is(.multiple-choice-options label):has(input:checked){
      border-color: ${appearance.textColor} !important;
    }

    :is(.multiple-choice-options label):hover:not(:has(input:checked)){
      border-color: ${appearance.inputHoverBorderColor} !important;
    }

    .survey-form input[type=text],
    .survey-form textarea {
      background-color: ${appearance.inputBackgroundColor} !important;
      color: ${appearance.textColor} !important;
      border-color: ${appearance.borderColor} !important;
    }

    .footer-branding {
      color: ${appearance.textColor} !important;
    }
  `;
  return true;
}

export const PostHogWrapper = ({ children }: Props): ReactElement => {
  const posthog = usePostHog();
  const { userInfo } = useAuthProvider();
  const theme = useTheme();
  const identifiedUserRef = useRef<string | null>(null);

  const themeMode = theme.palette.mode;

  // Keep identifiedUserRef in sync with user authentication state
  useEffect(() => {
    if (posthog && userInfo?.user?.id && identifiedUserRef.current !== userInfo.user.id) {
      posthog.identify(userInfo.user.id, {
        email: userInfo.user.email,
        name: userInfo.user.name,
        is_student: userInfo.user.signup_survey?.is_student,
      });
      identifiedUserRef.current = userInfo.user.id;
    } else if (!userInfo?.user?.id) {
      identifiedUserRef.current = null;
    }
  }, [posthog, userInfo]);

  // Watch for PostHog survey elements being added to the DOM
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) {
            // Check if this is a PostHog survey element
            if (node.className?.toString().startsWith('PostHogSurvey')) {
              applyStylesToSurvey(node, themeMode);
            }
            // Also check children in case it's wrapped
            const surveys = node.querySelectorAll('[class^="PostHogSurvey"]');
            surveys.forEach((el) => applyStylesToSurvey(el, themeMode));
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Also apply to any existing surveys
    document.querySelectorAll('[class^="PostHogSurvey"]').forEach((el) => applyStylesToSurvey(el, themeMode));

    return () => observer.disconnect();
  }, [themeMode]);

  return <>{children}</>;
};
